/**
 * Communicates with our Lambda functions on AWS.
 * Used to run logic we don't want the user to know about on the serverside
 */
import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { Http } from '@angular/http';
import { RestaurantInformation } from '../avantoeats-util/models/session/RestaurantInformation';

@Injectable({
  providedIn: 'root'
})
export class AvantoLambdaService {

  // private readonly LAMBDA_URL: string = "http://localhost:3000/dev";
  // private readonly LAMBDA_URL_NOTIFICATIONS: string = "http://localhost:3000";
  // private readonly LAMBDA_URL_FEES_DISCOUNTS: string  = "http://localhost:3000/dev"
  // private readonly LAMBDA_URL_PAYMENTS: string  = "http://localhost:3000/dev"

  private readonly LAMBDA_URL: string           = "https://3plp5rn5t4.execute-api.us-east-1.amazonaws.com/dev";
  private readonly LAMBDA_URL_NOTIFICATIONS: string = "https://v47p8tpa45.execute-api.us-east-1.amazonaws.com/dev";
  private readonly LAMBDA_URL_FEES_DISCOUNTS: string  = "https://wjzbofggo7.execute-api.us-east-1.amazonaws.com/dev";
  private readonly LAMBDA_URL_PAYMENTS: string  = "https://ryi039mdr1.execute-api.us-east-1.amazonaws.com/dev";

  constructor(private http: Http) { }

  //Restaurants
  fetchRestaurantList(custLatitude: number | null, custLongitude: number | null) {
    return this.http.post(this.LAMBDA_URL + "/restaurants/list", {
      customerLatitude: custLatitude,
      customerLongitude: custLongitude
    })
  }

  fetchSingleRestaurant(restaurantRegion: string, clientId: string, custLatitude: number | null, custLongitude: number | null) {
    return this.http.post(this.LAMBDA_URL + "/restaurants/getSingle/" + restaurantRegion + "/" + clientId, {
      customerLatitude: custLatitude,
      customerLongitude: custLongitude
    })
  }

  refreshRestaurantAvailability(restaurantInformation:RestaurantInformation[], custLatitude: number | null, custLongitude: number | null) {
    return this.http.post(this.LAMBDA_URL + "/restaurants/refreshAvailability/", {
      restaurantList: restaurantInformation,
      customerLatitude: custLatitude,
      customerLongitude: custLongitude
    })
  }

  checkIfRestaurantAvailable(sessionId){
    return this.http.post(this.LAMBDA_URL + "/restaurants/isRestaurantAvailable/", {
      sessionId: sessionId,
    })
  }

  getRestaurantProduct(restId, prodId) {
    return this.http.post(this.LAMBDA_URL + "/restaurants/getproduct", {
      restaurantId: restId,
      productId: prodId
    })
  }

  //=== CUSTOMER ===\\
  fetchCustomerInformation(custId: string, getLastOrders:boolean) {
    return this.http.post(this.LAMBDA_URL + "/customers/info", {
      customerId: custId,
      getLastOrders: getLastOrders
    });
  }

  deleteCustomerSavedAddress(custId: string, newSavedObject: any){
    return this.http.post(this.LAMBDA_URL + "/customers/deleteAddresses", {
      customerId: custId,
      savedAddresses: newSavedObject
    });
  }

  updateCustomerForNewOrder(saveOrderResponse:any) {
    return this.http.post(this.LAMBDA_URL + "/customers/updateForNewOrder", saveOrderResponse);
  }

  //=== SESSION ===\\
  getSession(id) {
    return this.http.post(this.LAMBDA_URL + "/session/get", {
      sessionId: id,
      origin:"website v"+environment.version
    });
  }

  /**
   * Adds a product to the cart in the session
   * @param sessionId ID of the session
   * @param clientId ID of the restaurant from which the product is being added
   * @param productId ID of the product being added
   * @param productQuantity How many of the product will be added
   * @param productVersion Version of the product being added
   * @param productNote (OPTIONAL) additional note related to this product
   * @param cartItemId (OPTIONAL) Used during EDITS. It's the ID of the cart item being edited.
   */
  setItemInSession(
    sessionId: string,
    clientId: string,
    productId: string,
    productQuantity: number,
    productVersion: number,
    productNote: string,
    productCustomizations: { customizationId: string, selectedOptions: { optionId: string, quantity: number }[] }[],
    cartItemId: string | null
  ) {
    const payload = {
      "sessionId": sessionId,
      "clientId": clientId,
      "productId": productId,
      "productQuantity": productQuantity,
      "productVersion": productVersion
    };
    if (cartItemId != null) {
      payload["cartItemId"] = cartItemId;
    }

    if (productNote != null && productNote != "") { payload["productNote"] = productNote }
    if (productCustomizations != null && productCustomizations.length > 0) {
      payload["customizations"] = productCustomizations;
    }

    return this.http.post(this.LAMBDA_URL + "/session/cart/set", payload)
  }

  /**
   * Removes a cart item from the session
   * @param sessionId ID of the session
   * @param cartItemIds array with IDs of the cart items being removed
   */
  removeItemFromSession(sessionId: string, cartItemIds: string[]) {
    return this.http.post(this.LAMBDA_URL + "/session/cart/remove", {
      "sessionId": sessionId,
      "cartItemId": cartItemIds
    })
  }

  /**
   * Validates a session, testing if we can perform a checkout or not
   * @param sessionId ID of the session
   */
  validateSession(sessionId: string) {
    return this.http.post(this.LAMBDA_URL + "/session/validate", {
      "sessionId": sessionId
    })
  }

  /**
   * Removes a cart item from the session
   * @param sessionId ID of the session
   * @param cartItemId ID of the cart item being removed
   */
  removeAllItemsFromSession(sessionId: string) {
    return this.http.post(this.LAMBDA_URL + "/session/cart/removeAll", {
      "sessionId": sessionId
    })
  }


  /**
   * Updates the session with a new address, received from google
   * @param id id of the session
   * @param selectedAddress string containing the selected address
   * @param addressNote additional note, related to the address
   * @param lat latitude
   * @param lng longitude
   */
  updateSessionAddress(id, selectedAddress, addressNote, lat, lng) {
    return this.http.post(this.LAMBDA_URL + "/session/update/address", {
      sessionId: id,
      address: selectedAddress,
      addressNote: addressNote,
      latitude: lat,
      longitude: lng
    })
  }

  /**
   * Updates the session with a new address, received from google
   * @param id id of the session
   * @param postalCode ...
   * @param street street name
   * @param streetNum street number
   * @param city ...
   * @param addressNote additional note, related to the address
   * @param lat latitude
   * @param lng longitude
   * @param isValid Whether the address has been validated on the checkout page or not
   */
  updateSessionCustomerInfo(id, updateSessionCustomerInfo) {
    return this.http.post(this.LAMBDA_URL + "/session/update/customerInfo", {
      sessionId: id,
      customerInfo: updateSessionCustomerInfo
    })
  }

  /**
   * Updates the session with a new order type
   * @param sessionId 
   * @param newOrderType 
   */
  updateSessionOrderType(sessionId, newOrderType) {
    return this.http.post(this.LAMBDA_URL + "/session/update/type", {
      sessionId: sessionId,
      orderType: newOrderType
    })
  }

  /**
   * Updates the session with a new order date
   * @param sessionId 
   * @param newOrderDate 
   */
  updateSessionOrderTime(sessionId, newOrderDate, delTimestamp?) {
    return this.http.post(this.LAMBDA_URL + "/session/update/date", {
      sessionId: sessionId,
      orderDate: newOrderDate,
      orderFutureDateTimestamp: delTimestamp
    })
  }

  /**
   * TODO: EXPLAIN
   * @param sessionId Id of the session
   * @param restId Restaurant ID (ex = whitby.greek-tycoon)
   * @param cartObject Cart object that will be set on the session
   */
  reorderCart(sessionId, restId, cartObject) {
    return this.http.post(this.LAMBDA_URL + "/session/cart/reOrder", {
      sessionId: sessionId,
      restaurantId: restId,
      cart: cartObject
    })
  }

  /**
   * Updates the session with a new order date
   * @param sessionId 
   */
  checkout(sessionId,paymentMethod:string, creditCardLast4Digits:string) {
    return this.http.post(this.LAMBDA_URL + "/checkout/saveOrder", {
      sessionId: sessionId,
      paymentMethod:paymentMethod,
      last4Digits:creditCardLast4Digits
    })
  }

  updateOneRestaurantModeInfo(sessId:string, useOneRestMode:boolean, clientId?:string ){
    return this.http.post(this.LAMBDA_URL + "/session/oneRestaurantModeUpdate",{
      sessionId:sessId,
      useOneRestaurantMode:useOneRestMode,
      clientId:clientId
    })
  }

  /**
   * Get the dates when the order can be scheduled
   */
  getScheduleOrderDates(){
    return this.http.get(this.LAMBDA_URL + "/futureOrder/getDates")
  }

  /** Sends an e-mail indicating that there was a review for the restaurant */
  sendReviewEmail(message:string,  restaurantId:string, restaurantTitle:string){
    return this.http.post(this.LAMBDA_URL_NOTIFICATIONS + "/review/email",{
      reviewMsg: message,
      type: "website",
      reviewedRestaurantId: restaurantId,
      reviewedRestaurantStr:restaurantTitle
    })
  }

  //=== PAYMENTS ===\\

    //Fees and discounts
    setTip(sessId:string, tipVal:number){
      return this.http.post(this.LAMBDA_URL_FEES_DISCOUNTS + "/fee/setTip",{
        sessionId: sessId,
        tipValue: tipVal
      })
    }
  
    //Fees and discounts
    applyCoupon(sessId:string, code:string){
      return this.http.post(this.LAMBDA_URL_FEES_DISCOUNTS + "/coupon/apply",{
        sessionId: sessId,
        couponCode: code,
        requestType: "website"
      })
    }
  
    //Fees and discounts
    removeCoupon(sessId:string){
      return this.http.post(this.LAMBDA_URL_FEES_DISCOUNTS + "/coupon/remove",{
        sessionId: sessId,
      })
    }

    //Square (Apple Pay / Google Pay on the checkout page)
    /** Gets the Square application and location the session pays into. The backend decides which Square account the restaurant uses. */
    getSquarePaymentInformation(sessionId:string){
      return this.http.post(this.LAMBDA_URL_PAYMENTS + "/squarepayment/v2",{
        operationType: "getPaymentInformation",
        sessionId: sessionId,
        isTesting: false
      })
    }

    /**
     * Charges a Square payment token (from Apple Pay or Google Pay) and places the order. The backend validates the order before charging.
     * @param last4DigitsOfCard Saved on the order, like the Square payment page does. Null when the wallet doesn't share it.
     * @param isRetry TRUE when retrying after a timeout. The backend reuses the same idempotency key, so the customer is never charged twice.
     */
    processSquarePayment(sessionId:string, sourceId:string, last4DigitsOfCard:string|null, isRetry:boolean){
      const requestBody:any = {
        operationType: "processPayment",
        sessionId: sessionId,
        sourceId: sourceId,
        //Wallet payments can't be saved as cards
        shouldSaveCreditCard: false,
        isRetry: isRetry,
        isTesting: false
      };
      if(last4DigitsOfCard != null && last4DigitsOfCard != ""){
        requestBody.last4DigitsOfCard = last4DigitsOfCard;
      }
      return this.http.post(this.LAMBDA_URL_PAYMENTS + "/squarepayment/v2", requestBody)
    }
}
